import { z } from "zod";
import { Advisor } from "../Entity/Types";
import { AttachmentId, Custodian, DateSchema, LocalDate, ServiceTeamId, TaskCommentId, TaskId, } from "../Types";
import { TASK_TITLE_MAX_LENGTH } from "./constants/common";
export const PersonName = z.object({
    first: z.string(),
    middle: z.string().nullable(),
    last: z.string(),
});
export const Person = z.object({
    id: z.string(),
    name: PersonName.nullable(),
});
export const NamedPerson = z.object({
    id: z.string(),
    name: PersonName,
});
export const AssigneeType = z.enum(["PERSON", "GROUP"]);
export const PersonAssignee = z.object({
    type: z.literal(AssigneeType.enum.PERSON),
    id: z.string(),
    personName: PersonName.nullable(),
});
export const GroupAssignee = z.object({
    type: z.literal(AssigneeType.enum.GROUP),
    id: z.string(),
    groupName: z.string().nullable(),
});
export const TaskAssignee = z.discriminatedUnion("type", [
    PersonAssignee,
    GroupAssignee,
]);
export const TaskManipulatorType = z.enum(["HUMAN", "SYSTEM"]);
export const TaskHumanManipulator = Person.extend({
    type: z.literal(TaskManipulatorType.enum.HUMAN),
});
export const TaskSystemManipulator = z.object({
    type: z.literal(TaskManipulatorType.enum.SYSTEM),
});
export const TaskManipulator = z.discriminatedUnion("type", [
    TaskHumanManipulator,
    TaskSystemManipulator,
]);
export const Attachment = z.object({
    id: AttachmentId,
    filename: z.string(),
    previewUrl: z.string().url(),
    downloadUrl: z.string().url(),
});
export const TaskStatus = z.enum([
    "NOT_STARTED",
    "IN_PROGRESS",
    "WAITING_ON_CLIENT",
    "WAITING_ON_ADVISOR",
    "WAITING_ON_THIRD_PARTY",
    "WAITING_ON_CUSTODIAN",
    "COMPLETED",
    "CANCELED",
]);
export const TaskType = z.enum([
    "ACCOUNT_OPENING",
    "ACCOUNT_MAINTENANCE",
    "ACCOUNT_TRANSFER",
    "TRADE_REQUEST",
    "ALTERNATIVE_INVESTMENT",
    "STANDING_INSTRUCTIONS",
    "MONEY_MOVEMENT",
    "LIQUIDATION",
    "OTHER",
]);
export const TaskTypeV2 = z.enum([
    "ACCOUNT_OPENING",
    "ACCOUNT_MAINTENANCE",
    "ACCOUNT_TRANSFER",
    "TRADE_REQUEST",
    "ALTERNATIVE_INVESTMENT",
    "STANDING_INSTRUCTIONS",
    "MONEY_MOVEMENT",
    "LIQUIDATION",
    "OTHER",
    "BUGS_AND_ENGINEERING_REQUESTS",
    "FINANCIAL_PLANNING",
    "IRA_ROLLOVERS",
    "BILLING",
    "CLIENT_REPORT",
]);
export const TaskTypeV3_1 = z.enum([
    "ACCOUNT_OPENING",
    "ACCOUNT_MAINTENANCE",
    "ACCOUNT_TRANSFER",
    "TRADE_REQUEST",
    "ALTERNATIVE_INVESTMENT",
    "STANDING_INSTRUCTIONS",
    "MONEY_MOVEMENT",
    "LIQUIDATION",
    "OTHER",
    "BUGS_AND_ENGINEERING_REQUESTS",
    "FINANCIAL_PLANNING",
    "IRA_ROLLOVERS",
    "BILLING",
    "CLIENT_REPORT",
    "TRANSITION_MASTER_MERGE",
    "TRANSITION_ACATS",
    "TRANSITION_LPOA",
    "TRANSITION_KICKOFF",
    "TRANSITION_TRANSITION",
    "TRANSITION_LAUNCH",
    "TRANSITION_POST_LAUNCH",
    "TRANSITION_ACCESS",
    "TRANSITION_ADVISOR_ONBOARDING",
]);
// NOTE: right now system task types are equal to the whole set, in future they might be a subset like manual ones
export const SystemTaskType = TaskType;
export const SystemTaskTypeV2 = TaskTypeV2;
export const ManualTaskType = TaskType.exclude(["LIQUIDATION"]);
export const ManualTaskTypeV2 = TaskTypeV2.exclude(["LIQUIDATION"]);
export const TaskPriority = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const TaskSource = z.enum([
    "MANUAL",
    "SYSTEM_GENERATED",
    "EMAIL_TO_TASK",
]);
export const TaskTitle = z.string().max(TASK_TITLE_MAX_LENGTH);
export const TaskBasicInfo = z.object({
    id: TaskId,
    title: TaskTitle,
    taskStatus: TaskStatus,
    client: Person.nullable(),
});
export const TaskChildren = z.object({
    tasks: TaskBasicInfo.array(),
});
export const Task = z.object({
    id: TaskId,
    assignee: TaskAssignee,
    advisor: Person.nullable(),
    creator: TaskManipulator,
    client: Person.nullable(),
    modifiedBy: TaskManipulator.nullable(),
    isSystemGenerated: z.boolean(),
    title: TaskTitle,
    description: z.string().nullable(),
    createdTs: z.string().datetime(),
    updatedTs: z.string().datetime(),
    completedTs: z.string().datetime().nullable(),
    dueDate: LocalDate.nullable(),
    custodian: Custodian.nullable(),
    custodianAccountNumber: z.string().nullable(),
    taskType: TaskType,
    taskStatus: TaskStatus,
    taskPriority: TaskPriority,
});
export const TaskV2 = Task.extend({
    assignee: TaskAssignee.nullable(),
    serviceTeamId: ServiceTeamId.nullable(),
    // NOTE: temporarily nullish to not to break the API
    parent: TaskBasicInfo.nullish(),
    children: TaskChildren.nullish(),
});
export const TaskV3 = TaskV2.omit({ taskType: true }).extend({
    taskType: TaskTypeV2,
    source: TaskSource.optional(),
});
export const TaskV3_1 = TaskV3.omit({ taskType: true }).extend({
    taskType: TaskTypeV3_1,
    advisors: Person.array(),
});
export const TaskDetails = Task.extend({
    attachments: Attachment.array(),
});
export const TaskDetailsV2 = TaskV2.extend({
    isDeleted: z.boolean(),
    attachments: Attachment.array(),
});
export const TaskDetailsV3 = TaskV3.extend({
    isDeleted: z.boolean(),
    attachments: Attachment.array(),
});
export const TaskDetailsV3_1 = TaskV3_1.extend({
    isDeleted: z.boolean(),
    attachments: Attachment.array(),
});
export const TaskComment = z.object({
    id: TaskCommentId,
    content: z.string(),
    author: Person.extend({
        headshotPath: z.string().url().nullable(),
    }),
    createdTs: z.string().datetime(),
    updatedTs: z.string().datetime(),
    attachments: Attachment.array(),
});
export const TaskCommentWithAuthorRoles = TaskComment.extend({
    author: TaskComment.shape.author.extend({
        roles: z.array(z.string()),
    }),
});
const HumanModifier = z.object({
    id: z.coerce.string(),
    type: z.literal("HUMAN"),
});
const HumanModifierWithProfile = HumanModifier.extend({
    profile: Advisor.optional(),
});
const SystemModifier = z.object({ type: z.literal("SYSTEM") });
const TaskActivityBase = z.object({
    activityId: z.string(),
    body: z.string(),
    timestamp: DateSchema,
});
export const TaskActivityWithModifierProfile = TaskActivityBase.extend({
    meta: z
        .object({
        data: z.object({
            version: z.coerce.string().transform((v) => Number(v)),
            modifier: z.discriminatedUnion("type", [
                HumanModifierWithProfile,
                SystemModifier,
            ]),
        }),
    })
        .optional(),
});
export const TaskActivity = TaskActivityBase.extend({
    meta: z
        .object({
        data: z.object({
            version: z.coerce.string().transform((v) => Number(v)),
            modifier: z.discriminatedUnion("type", [HumanModifier, SystemModifier]),
        }),
    })
        .optional(),
});
//# sourceMappingURL=Types.js.map